import { type RoleAwareUser, resolveAuthenticatedPath } from './userRoles';

export const AUTH_REDIRECT_STORAGE_KEY = 'auth_redirect_url';

function defaultOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return 'http://localhost';
}

export function rememberAuthRedirectTarget(target: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, target);
}

export function consumeAuthRedirectTarget(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const savedTarget = sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
  return savedTarget || undefined;
}

export function peekAuthRedirectTarget(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY) || undefined;
}

export function isOAuthAuthorizeRedirectTarget(target: string | undefined): boolean {
  return sanitizeAuthRedirectTarget(target)?.split('?', 1)[0] === '/oauth/authorize';
}

export function sanitizeAuthRedirectTarget(
  requestedTarget: string | undefined,
  origin = defaultOrigin()
): string | undefined {
  if (!requestedTarget) {
    return undefined;
  }

  const trimmedTarget = requestedTarget.trim();
  if (!trimmedTarget || trimmedTarget.startsWith('//')) {
    return undefined;
  }

  try {
    const parsedTarget = new URL(trimmedTarget, origin);
    if (parsedTarget.origin !== origin) {
      return undefined;
    }

    return `${parsedTarget.pathname}${parsedTarget.search}`;
  } catch {
    return undefined;
  }
}

export function resolveAuthenticatedRedirectTarget(
  user: RoleAwareUser,
  requestedTarget?: string,
  origin?: string
): string {
  return resolveAuthenticatedPath(user, sanitizeAuthRedirectTarget(requestedTarget, origin));
}
